// Supplier password reset (Lane 1442, Ryan 2026-11-05). Forgot: the supplier asks for a
// link by email; the server answers the same whether or not the email has an account.
// Reset: the link carries its token in the URL fragment (#t=...), which browsers never
// send to a server; the page reads it, removes it from the address bar, and posts it
// with the new password, then signs in.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicShell, AuthCard } from './PortalShell';
import {
  portalApi, setPortalToken, PASSWORD_MIN, PASSWORD_MAX, PORTAL_HOME, PORTAL_LOGIN, PORTAL_FORGOT,
} from './portalApi';

export function ForgotPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const r = await portalApi.requestReset(email.trim());
      setSent(r.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PublicShell>
      <AuthCard title="Reset your password" onSubmit={sent ? null : submit}
        subtitle={sent ? null : 'Enter the email you sign in with. We email you a link to set a new password.'}>
        {sent ? (
          <div className="srcp-banner srcp-banner--ok" role="status"><div className="srcp-grow">{sent}</div></div>
        ) : (
          <>
            <div className="srcp-field">
              <label className="srcp-label" htmlFor="srcp-forgot-email">Email</label>
              <input id="srcp-forgot-email" className="srcp-input" type="email" value={email} maxLength={255}
                autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
            </div>
            {error && <div className="srcp-error" role="alert">{error}</div>}
            <button type="submit" className="srcp-btn srcp-btn--primary" disabled={saving || !email.trim()}>
              {saving ? 'Sending…' : 'Email me a link'}
            </button>
          </>
        )}
        <div className="srcp-auth-foot"><Link className="srcp-link" to={PORTAL_LOGIN}>Back to sign in</Link></div>
      </AuthCard>
    </PublicShell>
  );
}

function readHashToken() {
  try {
    const hash = window.location.hash || '';
    return new URLSearchParams(hash.replace(/^#/, '')).get('t') || '';
  } catch (e) {
    return '';
  }
}

export function ResetPage() {
  const navigate = useNavigate();
  // Read in the initializer (pure); the address bar is cleaned in the effect.
  const [token] = useState(readHashToken);
  const [invalid, setInvalid] = useState(!token);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      setError(`Use a password of ${PASSWORD_MIN} to ${PASSWORD_MAX} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords are not the same.');
      return;
    }
    setSaving(true);
    try {
      const r = await portalApi.confirmReset(token, password);
      setPortalToken(r.token);
      navigate(PORTAL_HOME, { replace: true });
    } catch (err) {
      if (err.status === 404) { setInvalid(true); return; }
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (invalid) {
    return (
      <PublicShell>
        <AuthCard title="This link is not valid" subtitle="It may be used or expired. Ask for a new one.">
          <Link className="srcp-btn srcp-btn--primary" to={PORTAL_FORGOT}>Email me a new link</Link>
          <div className="srcp-auth-foot"><Link className="srcp-link" to={PORTAL_LOGIN}>Back to sign in</Link></div>
        </AuthCard>
      </PublicShell>
    );
  }
  return (
    <PublicShell>
      <AuthCard title="Set a new password" subtitle="This signs you out everywhere else." onSubmit={submit}>
        <div className="srcp-field">
          <label className="srcp-label" htmlFor="srcp-reset-pw">New password</label>
          <input id="srcp-reset-pw" className="srcp-input" type="password" value={password} maxLength={PASSWORD_MAX}
            autoComplete="new-password" placeholder={`${PASSWORD_MIN} characters or more`}
            onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="srcp-field">
          <label className="srcp-label" htmlFor="srcp-reset-pw2">Type it again</label>
          <input id="srcp-reset-pw2" className="srcp-input" type="password" value={confirm} maxLength={PASSWORD_MAX}
            autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} />
        </div>
        {error && <div className="srcp-error" role="alert">{error}</div>}
        <button type="submit" className="srcp-btn srcp-btn--primary" disabled={saving || !password || !confirm}>
          {saving ? 'Saving…' : 'Save and sign in'}
        </button>
      </AuthCard>
    </PublicShell>
  );
}
